import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Scissors, ArrowLeft, ScanFace, Shirt, Lock, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { MaskEditor } from "@/components/MaskEditor";
import { getAccess } from "@/utils/access";
import { subscribeTopInset } from "@/utils/telegram";
import { loadSession, saveSession, flushSession, clearSession } from "@/utils/session";

const reveal = {
  hidden: { opacity: 0, y: 40 },
  show: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.9, delay: 0.1 + i * 0.15, ease: [0.16, 1, 0.3, 1] },
  }),
};

export default function AppPreviewPage() {
  const [session, setSession] = useState(() => loadSession()); // молча продолжаем с последнего места (30 мин)
  const [photoUrl, setPhotoUrl] = useState(session?.photo || null);
  const [access, setAccess] = useState(null);
  // Стартуем всегда с редактора: детекции лица нужен смонтированный <img>.
  // Сохранённый экран (view) MaskEditor откроет сам после восстановления.
  const [view, setViewState] = useState("editor");
  const setView = (v) => {
    setViewState(v);
    saveSession({ view: v });
  };
  const [topInset, setTopInset] = useState(0);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    getAccess().then(setAccess);
  }, []);

  // Отступ под системный оверлей Telegram в полноэкранном режиме (0 в браузере)
  useEffect(() => subscribeTopInset(setTopInset), []);

  const goToPreorder = () => {
    navigate("/#preorder");
  };

  const normalizeImage = (file) =>
    new Promise((resolve, reject) => {
      const url = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => {
        const maxSide = 1600;
        const k = Math.min(1, maxSide / Math.max(img.naturalWidth, img.naturalHeight));
        const canvas = document.createElement("canvas");
        canvas.width = Math.round(img.naturalWidth * k);
        canvas.height = Math.round(img.naturalHeight * k);
        canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
        URL.revokeObjectURL(url);
        resolve(canvas.toDataURL("image/jpeg", 0.92));
      };
      img.onerror = reject;
      img.src = url;
    });

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try {
      const normalized = await normalizeImage(file);
      if (photoUrl) URL.revokeObjectURL(photoUrl);
      clearSession(); // новое фото = новая сессия
      setSession(null); // и забываем копию в памяти — редактор не должен восстановить чужой разбор
      setPhotoUrl(normalized);
      saveSession({ photo: normalized, view: "editor" });
      flushSession();
    } catch {
      toast.error("Не удалось прочитать фото. Попробуйте другое.");
    }
  };

  const resetPhoto = () => {
    if (photoUrl) URL.revokeObjectURL(photoUrl);
    clearSession();
    setSession(null);
    setView("editor");
    setPhotoUrl(null);
  };

  return (
    <div
      className={`flex flex-col bg-[#0A0A0A] ${photoUrl ? "h-dvh overflow-hidden" : "min-h-screen"}`}
      data-testid="studio-app-page"
    >
      <header
        className={`fixed inset-x-0 top-0 z-40 ${
          photoUrl && view === "editor"
            ? "bg-gradient-to-b from-[#0A0A0A]/70 to-transparent"
            : "border-b border-white/5 bg-[#0A0A0A]/60 backdrop-blur-xl"
        }`}
        style={{ top: topInset }}
      >
        <div className="flex items-center justify-between px-6 py-4 md:px-12">
          <Link to="/" className="flex items-center gap-3" data-testid="studio-logo">
            <Scissors size={15} strokeWidth={1.5} className="text-[#C2A878]" />
            <span className="font-jetbrains text-sm tracking-[0.35em] text-[#F4F4F0]">AFANASIEV</span>
          </Link>
          <Link
            to="/account"
            className="flex items-center gap-2 font-jetbrains text-xs uppercase tracking-[0.2em] text-[#A3A3A3] transition-colors duration-500 hover:text-[#C2A878]"
            data-testid="studio-account-link"
          >
            Кабинет
          </Link>
          <Link
            to="/"
            className="flex items-center gap-2 font-jetbrains text-xs uppercase tracking-[0.2em] text-[#A3A3A3] transition-colors duration-500 hover:text-[#C2A878]"
            data-testid="studio-back-link"
          >
            <ArrowLeft size={13} strokeWidth={1.5} />
            На лендинг
          </Link>
        </div>
      </header>

      <main
        className={
          photoUrl
            ? "relative flex flex-1 flex-col"
            : "relative flex flex-1 flex-col items-center justify-center overflow-hidden px-6 pb-20 pt-32"
        }
        style={photoUrl ? undefined : { paddingTop: 128 + topInset }}
      >
        <div
          aria-hidden="true"
          className="pointer-events-none absolute left-1/2 top-16 h-[500px] w-[700px] -translate-x-1/2 rounded-full bg-[#C2A878]/[0.05] blur-[130px]"
        />

        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          onChange={handleFile}
          className="hidden"
          data-testid="gallery-upload-input"
        />

        <AnimatePresence mode="wait">
          {access === null ? (
            <motion.div
              key="access-check"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex items-center justify-center"
              data-testid="app-access-check"
            >
              <Loader2 size={20} className="animate-spin text-[#C2A878]" />
            </motion.div>
          ) : access === false && !photoUrl ? (
            <motion.div
              key="gate"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0, y: -24 }}
              transition={{ duration: 0.5 }}
              className="flex flex-col items-center text-center"
              data-testid="app-gate-screen"
            >
              <motion.div variants={reveal} initial="hidden" animate="show" custom={0}>
                <motion.div
                  animate={{ y: [0, -14, 0], rotate: [-1.6, 1.6, -1.6] }}
                  transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
                >
                  <img
                    src="/images/mask-marq-3d-a.png"
                    alt="Маска Марквардта"
                    className="mask-glow w-60 mix-blend-screen md:w-80"
                    style={{
                      maskImage: "radial-gradient(circle at center, black 55%, transparent 75%)",
                      WebkitMaskImage: "radial-gradient(circle at center, black 55%, transparent 75%)",
                    }}
                    data-testid="gate-mask-image"
                  />
                </motion.div>
              </motion.div>
              <motion.h1
                variants={reveal}
                initial="hidden"
                animate="show"
                custom={1}
                className="mt-10 max-w-xl font-cormorant text-4xl font-light leading-[1.08] tracking-tight text-[#F4F4F0] md:text-5xl"
              >
                Персональный разбор начинается со знакомства
              </motion.h1>
              <motion.p
                variants={reveal}
                initial="hidden"
                animate="show"
                custom={2}
                className="mt-6 max-w-md text-base leading-relaxed text-[#A3A3A3]"
              >
                Имя и подтверждённый номер — и доступ ваш навсегда. В подарок —
                одна примерка новой стрижки на вашем фото.
              </motion.p>
              <motion.button
                variants={reveal}
                initial="hidden"
                animate="show"
                custom={3}
                onClick={goToPreorder}
                className="mt-12 flex items-center gap-3 border border-[#C2A878] px-12 py-4 font-jetbrains text-xs uppercase tracking-[0.3em] text-[#C2A878] transition-colors duration-500 hover:bg-[#C2A878] hover:text-[#0A0A0A]"
                data-testid="gate-preorder-button"
              >
                <Lock size={16} strokeWidth={1.5} />
                Получить доступ
              </motion.button>
              <motion.p
                variants={reveal}
                initial="hidden"
                animate="show"
                custom={4}
                className="mt-8 font-jetbrains text-[10px] uppercase tracking-[0.2em] text-[#525252]"
              >
                Один раз — больше не спросим
              </motion.p>
            </motion.div>
          ) : !photoUrl ? (
            <motion.div
              key="upload"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0, y: -24 }}
              transition={{ duration: 0.5 }}
              className="flex flex-col items-center text-center"
              data-testid="app-upload-screen"
            >
              <motion.div variants={reveal} initial="hidden" animate="show" custom={0}>
                <motion.div
                  animate={{ y: [0, -14, 0], rotate: [-1.6, 1.6, -1.6] }}
                  transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
                >
                  <img
                    src="/images/mask-marq-3d-a.png"
                    alt="Маска Марквардта"
                    className="mask-glow w-60 mix-blend-screen md:w-80"
                    style={{
                      maskImage: "radial-gradient(circle at center, black 55%, transparent 75%)",
                      WebkitMaskImage: "radial-gradient(circle at center, black 55%, transparent 75%)",
                    }}
                    data-testid="upload-mask-image"
                  />
                </motion.div>
              </motion.div>
              <motion.h1
                variants={reveal}
                initial="hidden"
                animate="show"
                custom={1}
                className="mt-10 max-w-xl font-cormorant text-4xl font-light leading-[1.08] tracking-tight text-[#F4F4F0] md:text-5xl"
              >
                Персональный ИИ-стилист: лицо, стрижки, гардероб
              </motion.h1>
              <motion.p
                variants={reveal}
                initial="hidden"
                animate="show"
                custom={2}
                className="mt-6 max-w-md text-base leading-relaxed text-[#A3A3A3]"
              >
                Точный разбор пропорций лица по маске Марквардта, примерка новых
                стрижек на вашем фото и образы из ваших собственных вещей.
              </motion.p>
              <motion.button
                variants={reveal}
                initial="hidden"
                animate="show"
                custom={3}
                onClick={() => inputRef.current?.click()}
                className="mt-12 flex items-center gap-3 bg-[#C2A878] px-12 py-4 font-jetbrains text-xs uppercase tracking-[0.3em] text-[#0A0A0A] transition-colors duration-500 hover:bg-[#D4BC8A]"
                data-testid="analyze-face-open-button"
              >
                <ScanFace size={16} strokeWidth={1.5} />
                Анализ лица
              </motion.button>
              <motion.button
                variants={reveal}
                initial="hidden"
                animate="show"
                custom={4}
                onClick={() => navigate("/wardrobe")}
                className="mt-4 flex items-center gap-3 border border-[#C2A878] px-12 py-4 font-jetbrains text-xs uppercase tracking-[0.3em] text-[#C2A878] transition-colors duration-500 hover:bg-[#C2A878] hover:text-[#0A0A0A]"
                data-testid="wardrobe-open-button"
              >
                <Shirt size={16} strokeWidth={1.5} />
                Гардероб
              </motion.button>
              <motion.p
                variants={reveal}
                initial="hidden"
                animate="show"
                custom={4}
                className="mt-8 font-jetbrains text-[10px] uppercase tracking-[0.2em] text-[#525252]"
              >
                Фото лица обрабатывается на вашем устройстве · в ИИ уходит только по кнопке анализа
              </motion.p>
            </motion.div>
          ) : (
            <motion.div
              key="editor"
              initial={{ opacity: 0, y: 32 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
              className="w-full"
            >
              <MaskEditor photoUrl={photoUrl} onNewPhoto={resetPhoto} view={view} setView={setView} topInset={topInset} restore={session} />
            </motion.div>
          )}
        </AnimatePresence>
      </main>

      {!photoUrl && (
        <footer className="border-t border-white/5 py-6 text-center">
          <p className="font-jetbrains text-[10px] uppercase tracking-[0.3em] text-[#525252]">
            © Afanasiev · Дизайн Про
          </p>
        </footer>
      )}
    </div>
  );
}
